import moment from 'moment';
import momentTimezone from 'moment-timezone';

import {
  dateTimeFormat,
  DDMMYYYYFormat,
  DDMMYYYYTimeFormat,
  DDMMYYYYHHMMTimeFormat,
  timezoneDiffTimeFormat,
  timezoneTextFormat,
  YYYYMMDDCompactFormat
} from './GlobalConstant';
import { getItemFromLocalStorage } from './localStorageUtils';

export const convertISOToDateFormat = (isoDate) => {
  var date = moment(isoDate);

  // check if date is valid and format is given
  if (date.isValid()) {
    const { hours = '' } = getTimeAdjustment();
    if (hours) {
      date = date.add(hours, 'hours');
    }
    return date.format(DDMMYYYYFormat);
  }

  return null;
};

export const convertDateInputToEpoch = (dateInput, isInUTC) => {
  // check if input is a valid date
  const date = moment(dateInput);
  if (date.isValid()) {
    // if timestamp is in UTC already
    if (isInUTC) {
      // input dateInput into moment's UTC together with the timestamp format given (ISO)
      // then return unix in milliseconds using valueOf()
      return moment(dateInput, [dateTimeFormat]).valueOf();
    }

    // convert dateInput to UTC and return unix milliseconds using valueOf()
    return moment.utc(dateInput).valueOf();
  }

  return null;
};

export const convertLocalTimeToEpoch = (dateInput) => {
  // check if input is a valid date
  const date = moment(dateInput);
  if (date.isValid()) {
    // convert dateInput to UTC and return unix milliseconds using valueOf()
    return moment(dateInput, dateTimeFormat).local(true).valueOf();
  }

  return null;
};

export const convertLocalEpochToUTC = (epochDate) => {
  const date = moment(epochDate);

  // check if date is valid and then return utc epoch date
  if (date.isValid()) {
    return moment(epochDate).utc().valueOf();
  }

  return null;
};

export const convertISOToTimeFormat = (isoTime) => {
  const time = moment(isoTime);

  // check if time is valid and format is given
  if (time.isValid()) {
    return time.format('HH:mm');
  }

  return null;
};

export const getLocalTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * PER-69 — per-client timezone for downloaded reports (case / alert / NS listings).
 *
 * Driven by the `download-timezone-mode` client config (see config.js):
 *   - 'utc'  -> 'UTC' : for clients that store local time AS-IF UTC and apply a FE
 *                       `timeAdjustment` (e.g. GCash). UTC then equals the raw
 *                       stored wall-clock = what the UI shows.
 *   - else   -> the browser timezone (the original, product-required behavior):
 *               the export matches the user's on-screen local time.
 *
 * Default (config key unset / any value other than 'utc') is the browser
 * timezone, so existing non-GCash clients are unchanged. NOTE: the case/alert/
 * retry-history Excel endpoints require a valid Time-Zone header (they return
 * BadRequest "Please provide a valid timezone to generate Report" if absent),
 * so callers must always send the value this returns.
 */
export const getDownloadTimezone = () =>
  getItemFromLocalStorage('download-timezone-mode') === 'utc'
    ? 'UTC'
    : getLocalTimezone();

export const convertISOToDateTimeFormat = (isoDate) => {
  var date = moment(isoDate);
  // check if date is valid and format is given
  if (date.isValid()) {
    const { hours = '' } = getTimeAdjustment();
    if (hours) {
      date = date.add(hours, 'hours');
    }

    return date.format(DDMMYYYYTimeFormat);
  }
};

export const getTimeAdjustment = () => {
  const timeAdjustment =
    getItemFromLocalStorage('time-adjustment') &&
    JSON.parse(getItemFromLocalStorage('time-adjustment'));

  if (timeAdjustment && timeAdjustment?.hours) {
    return {
      hours:
        timeAdjustment?.direction === 'minus'
          ? -timeAdjustment.hours
          : timeAdjustment.hours
    };
  }

  return {};
};

/**
 * PER-69 / FIN-10470 — align a date-range FILTER boundary with the
 * `timeAdjustment` that is applied to DISPLAYED timestamps.
 *
 * The date picker builds `from`/`to` from the browser-local calendar day, and
 * the display adds `timeAdjustment` hours (e.g. −8h for GCash, which stores
 * local time as-if-UTC). For the backend `created_timestamp` filter to return
 * the same day the user sees on screen (and in the export), the boundary must
 * invert that adjustment (apply `−timeAdjustment`). With no `timeAdjustment`
 * configured the value is returned unchanged, so non-GCash clients keep the
 * existing browser-local filtering behaviour.
 *
 * Used for ALL CM date/duration filters (per Yogesh's FIN-10470 directive):
 * case list, alert list, dashboard, investigator-workload, whitelist, and the
 * case/alert/operational-report downloads. It replaced convertLocalEpochToUTC
 * (an identity on epoch values), so it is behaviour-preserving for every client
 * that has no timeAdjustment configured.
 */
export const convertFilterEpochForBackend = (epochDate) => {
  const date = moment(epochDate);
  if (!date.isValid()) {
    return null;
  }
  const { hours = '' } = getTimeAdjustment();
  // Display does `date.add(hours, ...)`; invert it here. `hours` is already
  // signed ({direction:'minus'} → negative), so subtracting shifts a GCash
  // window +8h to match the displayed day.
  return hours ? date.subtract(hours, 'hours').valueOf() : date.valueOf();
};

/**
 * FIN-10470 — convenience wrapper for API modules: returns `params` with its
 * `from`/`to` epoch boundaries passed through convertFilterEpochForBackend.
 *
 * Null-safe by design: when `from`/`to` are absent (undefined/null) they are
 * left untouched — NEVER passed to moment() (moment(undefined) is "now" and
 * would silently inject a bogus boundary). All other params are preserved.
 */
export const convertFilterRangeParamsForBackend = (params = {}) => ({
  ...params,
  ...(params.from != null && {
    from: convertFilterEpochForBackend(params.from)
  }),
  ...(params.to != null && { to: convertFilterEpochForBackend(params.to) })
});

export const convertISOToDateTimeWithoutSeconds = (isoDate) => {
  var date = moment(isoDate);
  // check if date is valid and format is given
  if (date.isValid()) {
    const { hours = '' } = getTimeAdjustment();
    if (hours) {
      date = date.add(hours, 'hours');
    }
    return date.format(DDMMYYYYHHMMTimeFormat);
  }
};

export const getTimeDifferenceFromUTC = () =>
  moment().format(timezoneDiffTimeFormat);

export const getCurrentTimezoneText = () => {
  const currentTimezone = momentTimezone.tz.guess();

  return momentTimezone.tz(currentTimezone).format(timezoneTextFormat);
};

export const convertISOToDateTimeWithoutSecondsWithTZ = (isoDate) => {
  return (
    convertISOToDateTimeWithoutSeconds(isoDate) + ' ' + getCurrentTimezoneText()
  );
};

export const convertISOToUTCDateFormat = (isoDate) => {
  const date = moment(isoDate);

  // check if date is valid and format is given
  if (date.isValid()) {
    return date.utc().format(DDMMYYYYFormat);
  }

  return null;
};

export const formatTimeDifference = (notificationDate) => {
  const currentDate = new Date();
  const targetDate = new Date(notificationDate);
  const timeDifference = currentDate - targetDate;

  // Defined time intervals below
  const secondsInMs = 1000;
  const minutesInMs = secondsInMs * 60;
  const hoursInMs = minutesInMs * 60;
  const daysInMs = hoursInMs * 24;
  const monthsInMs = daysInMs * 30; // Approximation for a month

  if (timeDifference < minutesInMs) {
    // Less than a minute
    return `${Math.floor(timeDifference / secondsInMs)} secs ago`;
  } else if (timeDifference < hoursInMs) {
    // Less than an hour
    return `${Math.floor(timeDifference / minutesInMs)} mins ago`;
  } else if (timeDifference < daysInMs) {
    // Less than a day
    return `${Math.floor(timeDifference / hoursInMs)} hrs ago`;
  } else if (timeDifference < monthsInMs) {
    // Less than a month
    return `${Math.floor(timeDifference / daysInMs)} days ago`;
  } else {
    // More than a month
    const formattedDate = `${targetDate.getDate()}/${
      targetDate.getMonth() + 1
    }/${targetDate.getFullYear()}`;
    return formattedDate;
  }
};

export const convertISOToCompactDateFormat = (isoDate) => {
  const date = moment(isoDate);

  // check if date is valid and format is given
  if (date.isValid()) {
    return date.format(YYYYMMDDCompactFormat);
  }

  return null;
};
